import { Head, Link, useForm } from "@inertiajs/react"
import type { FormEvent } from "react"

import OAuthButton from "@/components/OAuthButton"
import AppLogo from "@/components/app-logo"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { newSessionPath, registrationPath } from "@/routes"

interface Props {
  email_address?: string
}

export default function New({ email_address }: Props) {
  const { data, setData, post, processing, errors } = useForm({
    name: "",
    email_address: email_address ?? "",
  })

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault()
    post(registrationPath())
  }

  return (
    <>
      <Head title="Sign Up" />

      <div className="vignette noise bg-bg-body flex min-h-screen items-center justify-center px-6 py-12">
        <div className="w-full max-w-md">
          <div className="mb-8 text-center">
            <Link href="/" className="inline-flex items-center justify-center">
              <AppLogo />
            </Link>
          </div>

          <div className="border-border bg-bg-card rounded-lg border p-8 shadow-lg">
            <h1 className="font-display mb-2 text-center text-2xl tracking-wide">
              Join the revolution.
            </h1>

            <p className="text-bone-muted mb-6 text-center text-sm">
              Stop hoarding unfinished tasks. No password needed.
            </p>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="name">Your name</Label>
                <Input
                  id="name"
                  type="text"
                  value={data.name}
                  onChange={(e) => setData("name", e.target.value)}
                  placeholder="Jane Doe"
                  required
                  autoFocus
                  autoComplete="name"
                />
                {errors.name && (
                  <p className="text-red text-sm">{errors.name}</p>
                )}
              </div>

              <div className="space-y-2">
                <Label htmlFor="email_address">Email address</Label>
                <Input
                  id="email_address"
                  type="email"
                  value={data.email_address}
                  onChange={(e) => setData("email_address", e.target.value)}
                  placeholder="you@example.com"
                  required
                  autoComplete="email"
                />
                {errors.email_address && (
                  <p className="text-red text-sm">{errors.email_address}</p>
                )}
              </div>

              <Button type="submit" className="w-full" disabled={processing}>
                Let&apos;s do this
              </Button>
            </form>

            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="border-border w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-bg-card text-bone-muted px-2">or</span>
              </div>
            </div>

            <div className="space-y-3">
              <OAuthButton provider="google_oauth2" action="sign_up" />
              <OAuthButton provider="apple" action="sign_up" />
            </div>

            <div className="mt-6 text-center">
              <p className="text-bone-muted text-sm">
                Already have an account?{" "}
                <Link
                  href={newSessionPath()}
                  className="text-bone hover:underline"
                >
                  Sign in
                </Link>
              </p>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}
